'use client';

import { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import Link from '@/components/shared/Link/Link';
import * as m from 'motion/react-m';
import { AnimatePresence } from 'motion/react';
import RichTextCopy from '@/components/shared/RichTextCopy/RichTextCopy';
import { constructHref } from '@/utils/linkHelpers';
import { ChevronDown } from '@1debit/icons';
import { cn } from '@/utils/styles';
import NavCarousel, { NavCarouselCardVariant } from '@/components/MainNav/NavCarousel';
import type { NavContentLinkProps } from '@/components/MainNav/types';
import type { ResourceProps } from '@/libs/contentful/types';
import { BlogNavigationProps } from './types';
import ChimeLogo from '@/components/shared/ChimeLogo/ChimeLogo';
import CTA from 'components/shared/CTA/CTA';
import { HEADER_NAV_ID_PREFIX } from '@/components/MainNav/utils';
import { useBreakpoint } from '@/hooks/useBreakpoints';

const CHEVRON_ROTATE_DURATION = 0.25;
const SUBNAV_SLIDE_DURATION = 0.3;
const BLOG_NAV_CAROUSEL_ID = 'blog_subnav_resources';

export type BlogNavProps = BlogNavigationProps;

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

function assignRef<T>(ref: React.Ref<T> | undefined, value: T | null): void {
  if (ref === undefined || ref === null) return;
  if (typeof ref === 'function') {
    ref(value);
  } else {
    ref.current = value;
  }
}

interface OpenSubnavCTAProps {
  ref?: React.Ref<HTMLButtonElement>;
  isOpen: boolean;
  panelId: string;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

const OpenSubnavCTA = ({ ref, isOpen, panelId, onClick }: OpenSubnavCTAProps) => (
  <button
    ref={ref}
    type="button"
    onClick={onClick}
    className={cn(
      'group cursor-pointer font-semibold font-sans text-inverse text-md p-0',
      'transition-colors duration-200 ease-out hover:text-fern',
    )}
    aria-expanded={isOpen}
    aria-controls={panelId}>
    <span
      className={cn(
        'relative inline-flex items-center gap-1 pb-0 hover:pb-0.5 transition-all duration-200 ease-in-out',
        'after:content-[""] after:absolute after:left-0 after:right-0 after:bottom-0 after:h-0.5 after:bg-fern',
        'after:origin-bottom after:opacity-0 after:transition-transform after:duration-200 after:ease-out group-hover:after:opacity-100',
      )}>
      <span>Explore</span>
      <m.span animate={{ rotate: isOpen ? -180 : 0 }} transition={{ duration: CHEVRON_ROTATE_DURATION }} aria-hidden>
        <ChevronDown className="shrink-0 size-(--ck-icon-size)" stroke="currentColor" strokeWidth={1.2} />
      </m.span>
    </span>
  </button>
);

interface SubnavPanelProps {
  isOpen: boolean;
  panelId: string;
  panelRef?: React.Ref<HTMLDivElement>;
  navItems: NavContentLinkProps[];
  hasResources: boolean;
  resourcesLabel?: string;
  resources: ResourceProps[];
  onClose: () => void;
}

const SubnavPanel = ({
  isOpen,
  panelId,
  panelRef,
  navItems,
  hasResources,
  resourcesLabel,
  resources,
  onClose,
}: SubnavPanelProps) => {
  const { isAboveSm } = useBreakpoint('sm');
  const localPanelRef = useRef<HTMLDivElement | null>(null);

  const setPanelRef = useCallback(
    (node: HTMLDivElement | null) => {
      localPanelRef.current = node;
      assignRef(panelRef, node);
    },
    [panelRef],
  );

  useLayoutEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const updateMaxHeight = () => {
      if (cancelled) return;
      const el = localPanelRef.current;
      if (!el) return;
      if (isAboveSm) {
        el.style.removeProperty('max-height');
        return;
      }
      const top = el.getBoundingClientRect().top;
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
      el.style.maxHeight = `${Math.max(0, Math.floor(viewportHeight - top))}px`;
    };

    const raf1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!cancelled) updateMaxHeight();
      });
    });

    const vv = window.visualViewport;
    vv?.addEventListener('resize', updateMaxHeight);
    window.addEventListener('resize', updateMaxHeight);
    window.addEventListener('scroll', updateMaxHeight, true);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      vv?.removeEventListener('resize', updateMaxHeight);
      window.removeEventListener('resize', updateMaxHeight);
      window.removeEventListener('scroll', updateMaxHeight, true);
      localPanelRef.current?.style.removeProperty('max-height');
    };
  }, [isOpen, isAboveSm]);

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: SUBNAV_SLIDE_DURATION }}
            className="absolute left-0 right-0 top-full z-10 hidden sm:block sm:h-screen sm:bg-scrim/60"
            onClick={onClose}
            aria-hidden
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpen && (
          <m.div
            ref={setPanelRef}
            id={panelId}
            role="region"
            aria-label="Blog taxonomy categories"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: SUBNAV_SLIDE_DURATION }}
            className="absolute left-0 right-0 top-full z-10 min-h-0 overflow-y-auto overflow-x-hidden overscroll-y-contain bg-primary sm:h-auto">
            <div className="max-w-[unset] content-wrapper mt-10 flex flex-col gap-6 py-4 sm:flex-row sm:py-6 sm:justify-between">
              <div className="flex flex-col gap-4 border-b border-content-secondary sm:border-0 pb-6">
                <span className="text-content-secondary font-semibold text-base">Categories</span>
                <div className="flex flex-col sm:flex-none sm:grid sm:grid-rows-6 sm:grid-flow-col gap-4 sm:gap-x-16 md:gap-x-40">
                  {navItems.map(item => {
                    const href = constructHref(item.link.url, item.link.internalPage);
                    if (!href) return null;
                    return (
                      <Link
                        key={item.sys?.id ?? item.link.label}
                        href={href}
                        className="text-xl font-sans font-bold text-inverse hover:text-fern">
                        {item.link.label}
                      </Link>
                    );
                  })}
                </div>
              </div>

              {hasResources && (
                <div className="max-w-72">
                  <NavCarousel
                    resourcesLabel={resourcesLabel ?? 'Featured'}
                    resourcesCollection={{ items: resources }}
                    resourcesInternalPage={{ slug: 'blog' }}
                    idPrefix={`${BLOG_NAV_CAROUSEL_ID}_${panelId}`}
                    cardVariant={NavCarouselCardVariant.MediumArticle}
                  />
                </div>
              )}
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </>
  );
};

const BlogNav = ({ navContent, navigationHeader, resourcesLabel, resourcesCollection, cta }: BlogNavProps) => {
  const [hybridSubnavOpen, setHybridSubnavOpen] = useState(false);
  const [initialSubnavOpen, setInitialSubnavOpen] = useState(false);
  const hybridPanelRef = useRef<HTMLDivElement>(null);
  const initialPanelRef = useRef<HTMLDivElement>(null);
  const hybridTriggerRef = useRef<HTMLButtonElement>(null);
  const initialTriggerRef = useRef<HTMLButtonElement>(null);

  const anySubnavOpen = hybridSubnavOpen || initialSubnavOpen;

  const trapFocus = useCallback((e: KeyboardEvent) => {
    const active = document.activeElement;
    const panel =
      [hybridPanelRef.current, initialPanelRef.current].find(p => p?.contains(active)) ??
      hybridPanelRef.current ??
      initialPanelRef.current;
    if (!panel) return;

    const focusable = panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  useEffect(() => {
    if (!anySubnavOpen) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        const activeElement = document.activeElement;
        const shouldFocusInitialTrigger =
          initialSubnavOpen &&
          (!hybridSubnavOpen ||
            initialPanelRef.current?.contains(activeElement) ||
            activeElement === initialTriggerRef.current);

        setHybridSubnavOpen(false);
        setInitialSubnavOpen(false);

        if (shouldFocusInitialTrigger) {
          initialTriggerRef.current?.focus();
        } else {
          hybridTriggerRef.current?.focus();
        }
      }
      if (e.key === 'Tab') trapFocus(e);
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [anySubnavOpen, hybridSubnavOpen, initialSubnavOpen, trapFocus]);

  const handleHybridSubnavClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setHybridSubnavOpen(prev => !prev);
  };

  const handleInitialSubnavClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setInitialSubnavOpen(prev => !prev);
  };

  const navItems = navContent?.items ?? [];
  const resources = resourcesCollection?.items ?? [];
  const hasResources = resources.length > 0;

  return (
    <>
      {/* Hybrid Nav — fixed to viewport top, always present but hidden behind Initial Nav until it scrolls away */}
      <nav
        className={cn(
          'fixed top-0 left-0 right-0 z-40 h-[var(--height-nav)] flex items-center bg-primary',
          !hybridSubnavOpen && 'border-b',
        )}
        aria-label="Blog home">
        <div className="max-w-[unset] content-wrapper flex flex-col gap-x-6 gap-y-2 py-3 mx-0 font-sans text-sm bg-primary">
          <div className="flex gap-4 items-center justify-between">
            <div className="flex gap-4 items-center">
              <Link href="/" id={`${HEADER_NAV_ID_PREFIX}_chime_logo`}>
                <ChimeLogo alt="Chime Home" />
              </Link>
              <div className="border-r border-content-secondary self-stretch mt-1" aria-hidden />
              <OpenSubnavCTA
                ref={hybridTriggerRef}
                isOpen={hybridSubnavOpen}
                panelId="blog-subnav-hybrid"
                onClick={handleHybridSubnavClick}
              />
            </div>
            {cta && (
              <div className="flex gap-4 items-center">
                <CTA cta={cta} className="min-w-0 px-5" id={`${HEADER_NAV_ID_PREFIX}_enroll_link`} />
              </div>
            )}
          </div>
        </div>

        <SubnavPanel
          isOpen={hybridSubnavOpen}
          panelId="blog-subnav-hybrid"
          panelRef={hybridPanelRef}
          navItems={navItems}
          hasResources={hasResources}
          resourcesLabel={resourcesLabel}
          resources={resources}
          onClose={() => setHybridSubnavOpen(false)}
        />
      </nav>

      {/* Initial Nav — in normal flow, z-50 above the fixed z-40 hybrid nav so it covers it initially */}
      <nav aria-label="In the Green categories" className="relative z-50 border-t py-2 bg-bg-primary">
        <div className="max-w-[unset] content-wrapper flex flex-col gap-x-6 gap-y-2 py-3 mx-0 font-sans text-sm">
          <div className="flex gap-4 items-center">
            <Link href="/blog" className="font-semibold text-inverse hover:text-primary hover:underline">
              <RichTextCopy copy={navigationHeader} />
            </Link>
            <div className="border-r border-content-secondary self-stretch" aria-hidden />
            <OpenSubnavCTA
              ref={initialTriggerRef}
              isOpen={initialSubnavOpen}
              panelId="blog-subnav-initial"
              onClick={handleInitialSubnavClick}
            />
          </div>
        </div>

        <SubnavPanel
          isOpen={initialSubnavOpen}
          panelId="blog-subnav-initial"
          panelRef={initialPanelRef}
          navItems={navItems}
          hasResources={hasResources}
          resourcesLabel={resourcesLabel}
          resources={resources}
          onClose={() => setInitialSubnavOpen(false)}
        />
      </nav>
    </>
  );
};

export default BlogNav;
